/**
 * @file validate.js
 * @description Input validation and formatting helpers for email, phone, and ZIP code.
 *   All validators return { valid: boolean|null, message: string } — null means empty/not filled.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module utils/validate
 */
// ─── Input validation helpers ─────────────────────────────────────────────────

// Email — RFC-lite: local@domain.tld
export function validateEmail(value) {
  if (!value || !value.trim()) return { valid: null, message: "" };
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!re.test(value.trim()))
    return { valid: false, message: "Enter a valid email address" };
  return { valid: true, message: "" };
}

// US Phone — accepts 10 raw digits in any spacing/dash format
export function validatePhone(value) {
  if (!value || !value.trim()) return { valid: null, message: "" };
  const digits = value.replace(/\D/g, "");
  if (digits.length < 10)
    return { valid: false, message: "Phone number must have 10 digits" };
  if (digits.length > 11)
    return { valid: false, message: "Phone number is too long" };
  if (digits.length === 11 && digits[0] !== "1")
    return { valid: false, message: "US numbers start with 1" };
  return { valid: true, message: "" };
}

// Format raw digits → (XXX) XXX-XXXX as user types
export function formatPhone(raw) {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

// US ZIP — 5 digits or ZIP+4 (12345 or 12345-6789)
export function validateZip(value) {
  if (!value || !value.trim()) return { valid: null, message: "" };
  const re = /^\d{5}(-\d{4})?$/;
  if (!re.test(value.trim()))
    return { valid: false, message: "Enter a valid 5-digit ZIP (or ZIP+4)" };
  return { valid: true, message: "" };
}

// Format ZIP — allow only digits and hyphen, cap at 10 chars
export function formatZip(raw) {
  const digits = raw.replace(/[^\d-]/g, "");
  // Auto-insert hyphen after 5 digits
  if (/^\d{6,9}$/.test(digits.replace("-", ""))) {
    const d = digits.replace("-", "");
    return `${d.slice(0,5)}-${d.slice(5)}`;
  }
  return digits.slice(0, 10);
}
